import React, {useEffect} from "react";
import GenericModal from "./GenericModal";
import { getOptionText, getOptionValueByText } from "../../utils/optionUtils";

const initialForm = {
  loanTypeCode: "",
  loanTypeName: "",
  interestRate: "0",
  minLoanAmount: "0",
  maxLoanAmount: "0",
  maxInstallment: "0",
  lateFees: "0",
  lateFeesType: "",
  rebate: "0",
  rebateTiming: "",
  penaltyIntRate: "0",
  installmentType: "",
  remarks: "",
  active: true,
  editable: true,
};

const AddLoanTypeModalNew = ({
  optionData = [],
  optionLoading,
  optionError,
  open,
  onClose,
  onSave,
  data,
}) => {
  const validateForm = (form) => {
    const newErrors = {};
    const requiredFields = [
      "loanTypeCode",
      "loanTypeName",
      "interestRate",
      "minLoanAmount",
      "maxLoanAmount",
      "maxInstallment",
      "lateFees",
      "lateFeesType",
      "rebate",
      "rebateTiming",
      "penaltyIntRate",
      "installmentType",
    ];
    const fieldLabels = {
      loanTypeCode: "Loan Type Code",
      loanTypeName: "Loan Type Name",
      interestRate: "Interest Rate",
      minLoanAmount: "Minimum Loan Amount",
      maxLoanAmount: "Maximum Loan Amount",
      maxInstallment: "Max Installments",
      lateFees: "Late Fees",
      lateFeesType: "Late Fees Type",
      rebate: "Rebate",
      rebateTiming: "Rebate Timing",
      penaltyIntRate: "Penalty Interest Rate",
      installmentType: "Installment Type",
      remarks: "Remarks",
    };
    const numericFields = ["interestRate", "minLoanAmount", "maxLoanAmount", "maxInstallment", "lateFees", "rebate", "penaltyIntRate"];

    requiredFields.forEach((field) => {
      if (!form[field]?.toString().trim()) {
        newErrors[field] = `${fieldLabels[field] || field} is required`;
      }
    });

    numericFields.forEach((field) => {
      if (
        form[field] === "" ||
        isNaN(Number(form[field])) ||
        Number(form[field]) <= 0
      ) {
        newErrors[field] = `${
          fieldLabels[field] || field
        } must be a number greater than 0`;
      }
    });

    if (
      form.minLoanAmount !== "" &&
      form.maxLoanAmount !== "" &&
      !isNaN(Number(form.minLoanAmount)) &&
      !isNaN(Number(form.maxLoanAmount)) &&
      Number(form.minLoanAmount) >= Number(form.maxLoanAmount)
    ) {
      newErrors.maxLoanAmount = "Maximum must be greater than minimum";
    }

    return newErrors;
  };

  const mapFormValues = (form, optionData) => {
    // Dynamic mapping via loop
    const mappings = [
      { field: "lateFeesType", optionName: "Late Fee Type" },
      { field: "rebateTiming", optionName: "Rebate Timing" },
      { field: "installmentType", optionName: "Installment Type" },
    ];

    const mappedForm = { ...form };
    mappings.forEach(({ field, optionName }) => {
      if (mappedForm[field]) {
        mappedForm[field] = getOptionText(optionData, optionName, mappedForm[field]);
      }
    });
    return mappedForm;
  };

  // New function to map option values when editing
  const mapOptionValues = (data, optionData) => {
    const lateFeesTypeValue = getOptionValueByText(optionData,"Late Fee Type", data.lateFeesType);
    const rebateTimingValue = getOptionValueByText(optionData,"Rebate Timing", data.rebateTiming);
    const installmentTypeValue = getOptionValueByText(optionData,"Installment Type", data.installmentType);
    
    return {
      id: data.id || null,
      createdAt: data.createdAt || null,
      loanTypeCode: data.loanTypeCode || "",
      loanTypeName: data.loanTypeName || "",
      interestRate: data.interestRate || "0",
      minLoanAmount: data.minLoanAmount || "",
      maxLoanAmount: data.maxLoanAmount || "",
      maxInstallment: data.maxInstallment || "",
      lateFees: data.lateFees || "",
      lateFeesType: lateFeesTypeValue || "",
      rebate: data.rebate || "",
      rebateTiming: rebateTimingValue || "",
      penaltyIntRate: data.penaltyIntRate || "",
      installmentType: installmentTypeValue || "",
      remarks: data.remarks || "",
      active: data.active ?? true,
      editable: data.editable ?? true,
    };
  };

  const fields = [
    {
      name: "loanTypeCode",
      label: "Loan Type Code",
      helper: "Unique identifier",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "loanTypeName",
      label: "Loan Type Name",
      helper: "Descriptive name",
      size: { xs: 12, sm: 12, md:6 }
    },
    {
      name: "interestRate",
      label: "Annual Interest Rate",
      helper: "e.g. 10",
      adornment: "%",
      type: "number",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "minLoanAmount",
      label: "Minimum Loan Amount",
      type: "number",
      helper: "e.g. 1000",
      adornment: "₹",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "maxLoanAmount",
      label: "Maximum Loan Amount",
      type: "number",
      helper: "e.g. 100000",
      adornment: "₹",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "maxInstallment",
      label: "Max Installments",
      type: "number",
      helper: "Max number of EMIs",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "installmentType",
      label: "Installment Type",
      type: "select",
      helper: "Select Installment Type",
      optionName: "Installment Type",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "penaltyIntRate",
      label: "Penal ROI (%)",
      type: "number",
      helper: "Penalty interest rate",
      adornment: "%",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "rebate",
      label: "Rebate (%)",
      helper: "Rebate",
      type: "number",
      adornment: "%",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "rebateTiming",
      label: "Rebate Timing",
      type: "select",
      helper: "Select rebate timing",
      optionName: "Rebate Timing",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "lateFeesType",
      label: "Late Fees Type",
      type: "select",
      helper: "Select late fees type",
      optionName: "Late Fee Type",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "lateFees",
      label: "Late Fees (₹)",
      type: "number",
      helper: "Fixed late fee",
      adornment: "₹",
      size: { xs: 12, sm: 12, md:6 }
    },
    {
      name: "remarks",
      label: "Description / Remarks",
      helper: "Optional",
      multiline: true,
      rows: 2,
      size: { xs: 12, sm: 12, md:12 }
    },
  ];

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      onSave={onSave}
      data={data ? mapOptionValues(data, optionData) : null} 
      title="Loan Type"
      fields={fields}
      initialForm={initialForm}
      validateForm={validateForm}
      mapFormValues={mapFormValues}
      optionData={optionData}
      saveButtonText="Save Loan"
      updateButtonText="Update Loan"
    />
  );
};

export default AddLoanTypeModalNew;
